import {Injectable} from '@angular/core';
import {Observable, of, retry} from 'rxjs';
import {delay, map, mergeMap, take, tap} from 'rxjs/operators';
import moment from 'moment';
import {ConsoleSessionId, DeviceId, ProjectId, TestRunId, UserId} from '@webmate-web-core/shared/types/ids';
import {SingleTestRunCreationSpec} from '@webmate-web-core/shared/types/testmgmt';
import {ValueShareService} from '@webmate-web-core/shared/services/valueshare.service';
import {TestService} from '@webmate-web-core/shared/services/test.service';
import {WmNotificationService} from "@webmate-web-core/shared/components/notification/wm-notification.service";

export enum TestRunStatus {
    PASSED = 'passed',
    FAILED = 'failed'
}

export interface TestRunReportData {
    testRunId: TestRunId;
    creator: UserId;
    creationTime: string;
    name: string;
    executionId: string;
}

@Injectable()
export class ConsoleTestRunService {

    public static readonly ADHOC_TESTNAME = 'Default AdHoc Test';

    private activeTestRun?: TestRunId;

    constructor(private valueShareService: ValueShareService, private testService: TestService, private notificationService: WmNotificationService) {
    }

    // make given testRun the currently active testrun (for this service) and inform other consumers of valueShareService
    activateTestRun(testRunId: TestRunId) {
        this.activeTestRun = testRunId;
        this.valueShareService.setValue('activeTestRun', testRunId);
    }

    deactivateTestRun() {
        this.activeTestRun = undefined;
        this.valueShareService.setValue('activeTestRun', undefined);
    }

    checkActiveTestRuns(projectId: ProjectId, userId: UserId, deviceId: DeviceId) {
        // Normally we would need a permission check to finish the test run here, but the active test runs are just for the current user,
        // and the assumption is that a user who can start a test also can finish it
        this.getActiveAdhocTestRunsForUserOnDevice$(projectId, userId, deviceId).pipe(take(1)).subscribe(res => {
            if (res.length > 0) {
                const testRun = res[0];
                let timestamp = moment(testRun.creationTime).fromNow();
                this.notificationService.openHtmlMessage(
                    'You have started a test run' +
                    ((testRun.name === 'Unnamed Test') ? '' : ' named <span style="font-weight: bold; color: var(--wm-text-primary)">\'' + testRun.name + '\'</span>') +
                    ' on this device <span style="font-weight: bold; color: var(--wm-text-primary)">' + timestamp + '</span>.' +
                    '<br>This test run is not finished yet, do you want to finish or resume it now?',
                    'Finish Test Run',
                    () => this.testService.finishTestRun(testRun.testRunId).subscribe(),
                    'Resume Test Run',
                    () => this.activateTestRun(testRun.testRunId),
                    'Ask again next time'
                );
            }
        });
    }

    getActiveAdhocTestRunsForUserOnDevice$(projectId: ProjectId, userId: UserId, deviceId: DeviceId): Observable<TestRunReportData[]> {
        const query = {
            aggregationSpec: [],
            groupingSpec: [],
            filterSpec: {
                deviceFilterSpecs: [],
                tagFilterSpecs: [],
                statusFilterSpec: ['pending_passed', 'pending_failed'],
                testTypeFilterSpec: ['adhoc'],
                userFilterSpec: [userId],
                timeSpec: {
                    type: 'RelativeTimeFilterSpec',
                    relativeTime: 'pastyear'
                }
            }
        };

        return this.testService.queryAdhocTestReports$(projectId, query).pipe(
            map((res: any) => {
                const items = res?.data?.items ?? [];
                return items.filter((run: any) => (run['devices'] ?? []).some((d: any) => d.id === deviceId));
            }));
    }


    /**
     * Return active testrun id if there is an active TestRun. Otherwise create a new one and return its id. Complete, after
     * the active testrun has been emitted.
     */
    ensureActiveTestRun(projectId: ProjectId, consoleSessionId: ConsoleSessionId, deviceId: DeviceId): Observable<TestRunId> {
        if (!!this.activeTestRun) {
            return of(this.activeTestRun);
        } else {
            let testRunCreationSpec = {
                type: 'SingleTestRunCreationSpec',
                assignmentSpec: {
                    consoleSessionId: {
                        type: 'String',
                        data: consoleSessionId
                    }, deviceId: {
                        type: 'String',
                        data: deviceId
                    }
                }
            } as SingleTestRunCreationSpec;

            return this.testService.createAndStartTestExecutionFromNamedTemplate(projectId, ConsoleTestRunService.ADHOC_TESTNAME, 'Console Ad-Hoc Test at ' + moment().format('DD.MM.YYYY HH:mm'), testRunCreationSpec).pipe(
                map(result => result.testRunId),
                delay(2500),
                mergeMap(testRunId => {
                    return this.testService.fetchTestRunWithRetry(testRunId!, 1000).pipe(map(tr => tr.testRunId));
                }),
                tap(testRun => {
                    this.activateTestRun(testRun);
                }, error => {
                    console.warn('Unable to start adhoc testrun', error);
                    this.deactivateTestRun();
                }),
                take((1)));
        }
    }

}
